import { relations } from 'drizzle-orm';
import { index, int, real, sqliteTable, text } from 'drizzle-orm/sqlite-core';

const randomId = () => Math.random().toString(36).slice(2);

const now = () => new Date();

export type Instance = typeof instance.$inferSelect;
export const instance = sqliteTable('instance', {
	id: text('id').primaryKey().$type<''>().default(''),
	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	seed: text('seed').notNull().$defaultFn(randomId)
});

export type Profile = typeof profiles.$inferSelect;
export const profiles = sqliteTable('profile', {
	id: text('id').primaryKey().default(''),
	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	displayName: text('display_name').notNull(),
	avatarStyle: text('avatar_style').notNull()
});

export type Provider = typeof providers.$inferSelect;
export const providers = sqliteTable('provider', {
	id: text('id').primaryKey().$defaultFn(randomId),
	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	title: text('title').notNull(),
	apiEndpointUrl: text('api_endpoint_url').notNull(),
	apiFlavor: text('api_flavor', {
		enum: ['open-router', 'x-ai', 'anthropic', 'mistral', 'ollama']
	}).notNull(),
	apiKey: text('api_key').notNull(),
	isDeleted: int('is_deleted', { mode: 'boolean' }).notNull().default(false),
	isEnabled: int('is_enabled', { mode: 'boolean' }).notNull().default(true),
	isPreset: int('is_preset', { mode: 'boolean' }).notNull().default(false),
	denyDataCollection: int('deny_data_collection', { mode: 'boolean' }).notNull().default(true)
});

export type Prompt = typeof prompts.$inferSelect;
export const prompts = sqliteTable(
	'prompt',
	{
		id: text('id').primaryKey().$defaultFn(randomId),
		createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
		updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

		title: text('title').notNull().default(''),
		isDeleted: int('is_deleted', { mode: 'boolean' }).notNull().default(false),
		isEnabled: int('is_enabled', { mode: 'boolean' }).notNull().default(true),
		isDefault: int('is_default', { mode: 'boolean' }).notNull().default(false),
		isPreset: int('is_preset', { mode: 'boolean' }).notNull().default(false),
		content: text('content').notNull().default('')
	},
	(table) => [
		index('is_deleted_is_enabled_is_default_idx').on(
			table.isDeleted,
			table.isEnabled,
			table.isDefault
		)
	]
);

export type Chat = typeof chats.$inferSelect;
export const chats = sqliteTable('chat', {
	id: text('id').primaryKey().$defaultFn(randomId),
	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	title: text('title').notNull().default(''),
	isDeleted: int('is_deleted', { mode: 'boolean' }).notNull().default(false),
	inferenceSettingsId: text('inference_settings_id')
		.notNull()
		.references(() => inferenceSettings.id),
	systemPrompt: text('system_prompt').notNull().default('')
});

export const chatsRelations = relations(chats, ({ one, many }) => ({
	interactions: many(chatInteractions),
	inferenceSettings: one(inferenceSettings, {
		fields: [chats.inferenceSettingsId],
		references: [inferenceSettings.id]
	})
}));

export type InferenceSettings = typeof inferenceSettings.$inferSelect;
export const inferenceSettings = sqliteTable(
	'inference_settings',
	{
		id: text('id').primaryKey().$defaultFn(randomId),
		createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
		updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

		isDefault: int('is_default', { mode: 'boolean' }).notNull().default(false),
		isPreset: int('is_preset', { mode: 'boolean' }).notNull().default(false),
		providerId: text('provider_id').notNull(),
		modelId: text('model_id').notNull(),
		temperature: real('temperature').notNull().default(1.0),
		topP: real('top_p').notNull().default(0.9),
		topK: int('top_k').notNull().default(0),
		frequencyPenalty: real('frequency_penalty').notNull().default(0.0),
		presencePenalty: real('presence_penalty').notNull().default(0.0),
		lastUsedAt: int('last_used_at', { mode: 'timestamp' }).notNull().$defaultFn(now)
	},
	(table) => [index('last_used_at_idx').on(table.lastUsedAt)]
);

export const inferenceSettingsRelations = relations(inferenceSettings, ({ one }) => ({
	provider: one(providers, {
		fields: [inferenceSettings.providerId],
		references: [providers.id]
	})
}));

export type Tool = typeof tools.$inferSelect;
export const tools = sqliteTable('tool', {
	id: text('id').primaryKey().$defaultFn(randomId),
	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	title: text('title').notNull(),
	identifier: text('identifier', {
		enum: ['browse_web', 'search_web', 'calculate', 'generate_image']
	}).notNull(),
	description: text('description').notNull(),
	isEnabled: int('is_enabled', { mode: 'boolean' }).notNull().default(true),
	isDeleted: int('is_deleted', { mode: 'boolean' }).notNull().default(false)
});

export type ChatInteraction = typeof chatInteractions.$inferSelect;
export const chatInteractions = sqliteTable('chat_interaction', {
	id: text('id').primaryKey().$defaultFn(randomId),
	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	chatId: text('chat_id')
		.notNull()
		.references(() => chats.id),
	type: text('type', { enum: ['text', 'error', 'tool-call'] }).notNull(),
	mimeType: text('mime_type').notNull().default(''),
	author: text('author', { enum: ['user', 'assistant', 'quill'] }).notNull(),
	isDeleted: int('is_deleted', { mode: 'boolean' }).notNull().default(false),
	content: text('content').notNull(),
	reasoning: text('reasoning').notNull().default(''),
	generatingModelId: text('generating_model_id').notNull().default(''),
	inputTokens: int('input_tokens').notNull().default(0),
	outputTokens: int('output_tokens').notNull().default(0),
	inferenceDurationMs: int('inference_duration_ms').notNull().default(0),
	timeToFirstTokenMs: int('time_to_first_token_ms').notNull().default(0),
	inputCostCents: int('input_cost_cents').notNull().default(0),
	outputCostCents: int('output_cost_cents').notNull().default(0)
});

export const chatInteractionsRelations = relations(chatInteractions, ({ one, many }) => ({
	chat: one(chats, {
		fields: [chatInteractions.chatId],
		references: [chats.id]
	}),
	attachments: many(chatInteractionAttachments, {
		relationName: 'chat-interaction-attachment-relation'
	})
}));

export type Automation = typeof automations.$inferSelect;
export const automations = sqliteTable('automations', {
	id: text('id').primaryKey().$defaultFn(randomId),
	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	isEnabled: int('is_enabled', { mode: 'boolean' }).notNull().default(true),

	title: text('title').notNull(),
	description: text('description').notNull(),

	inferenceSettingsId: text('inference_settings_id').notNull().default('default')
});

export const automationsRelations = relations(automations, ({ one }) => ({
	inferenceSettings: one(inferenceSettings, {
		fields: [automations.inferenceSettingsId],
		references: [inferenceSettings.id]
	})
}));

export type ChatInteractionAttachment = typeof chatInteractionAttachments.$inferSelect;
export const chatInteractionAttachments = sqliteTable('chat_interaction_attachments', {
	id: text('id').primaryKey().$defaultFn(randomId),
	chatInteractionId: text('chat_interaction_id')
		.notNull()
		.references(() => chatInteractions.id),

	createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(now),
	updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(now),

	isDeleted: int('is_deleted', { mode: 'boolean' }).notNull().default(false),

	mimeType: text('mime_type').notNull(),
	blob: text('blob').notNull()
});

export const chatInteractionAttachmentsRelations = relations(
	chatInteractionAttachments,
	({ one }) => ({
		chatInteraction: one(chatInteractions, {
			fields: [chatInteractionAttachments.chatInteractionId],
			references: [chatInteractions.id],
			relationName: 'chat-interaction-attachment-relation'
		})
	})
);
